import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';

import { Checkbox } from 'common/components/Checkbox';
import { Chip } from 'common/components/Chip';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { TableCell } from 'common/components/Table';
import { IGlobalDrawerType } from 'common/types';
import {
    IBasicUserFragment,
    IContentTypeValue,
    IPortfolioItemChildFragment,
    IPortfolioItemMatrixFragment,
    ISkillCertificateFragment,
    ISkillInputOptionFragment,
} from 'graphql/types';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { UserAvatar } from 'user/components/UserAvatar';

interface IProps {
    certificates: ISkillCertificateFragment[];
    isSelected?: boolean;
    portfolioItem?: IPortfolioItemMatrixFragment;
    portfolioItemChildren?: IPortfolioItemChildFragment[];
    user: IBasicUserFragment;
    skill?: ISkillInputOptionFragment | null;
    onSelect?(): void;
}

export const SkillCertificateMatrixRow = ({
    certificates,
    isSelected,
    portfolioItem,
    portfolioItemChildren = [],
    user,
    skill,
    onSelect,
}: IProps) => {
    const { openGlobalDrawer } = useGlobalDrawer();
    const client = useApolloClient();

    const handleRefetch = useCallback(() => {
        if (!portfolioItem) return;

        client.cache.evict({
            id: client.cache.identify({
                __typename: 'PortfolioItem',
                id: portfolioItem.id,
            }),
            fieldName: 'children',
        });
        client.cache.gc();
    }, [client, portfolioItem]);

    const handleCellClick = useCallback(
        (
            child: IPortfolioItemChildFragment | undefined,
            certificate: ISkillCertificateFragment
        ) => {
            const isReceivedCertificate =
                child?.relatedType === IContentTypeValue.ReceivedCertificate;

            openGlobalDrawer({
                type: IGlobalDrawerType.ReceivedCertificate,
                itemId: child?.id,
                props: {
                    canGenerateCertificate: true,
                    title: child?.title ?? certificate.certificate?.name,
                    user,
                    skill,
                    certificateId: isReceivedCertificate
                        ? child?.metadata?.certificateId
                        : (child?.relatedId ?? certificate.certificate?.id),
                    isExternalCertificate: child
                        ? child.metadata?.isExternalCertificate
                        : certificate.certificate?.isExternalCertificate,
                    requiresValidation: child?.metadata?.requiresValidation,
                    onDeleteCertificate: handleRefetch,
                    onGenerateCertificate: handleRefetch,
                    onSetExternalStatus: handleRefetch,
                },
            });
        },
        [openGlobalDrawer, user, skill, handleRefetch]
    );

    return (
        <TableRow>
            <TableCell padding="checkbox">
                <Checkbox
                    checked={!!isSelected}
                    onClick={(e) => {
                        onSelect?.();
                        e.stopPropagation();
                    }}
                />
            </TableCell>

            <TableCell
                scope="row"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                    openGlobalDrawer({
                        type: IGlobalDrawerType.UserProfile,
                        itemId: user.id,
                    });
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <UserAvatar size={45} user={user} />
                    <Typography sx={{ ml: 1.5 }} variant="body2">
                        {user.name}
                    </Typography>
                </Box>
            </TableCell>

            {certificates.map((sc) => {
                const child = portfolioItemChildren.find(
                    (c) => c.metadata?.certificateId === sc.certificate?.id
                );

                const showMissingIndicator = !child && sc.isMandatory;

                return (
                    <TableCell
                        align="center"
                        key={sc.certificate?.id}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleCellClick(child, sc)}
                    >
                        {showMissingIndicator ? (
                            <Chip
                                color="error"
                                icon={<CloseIcon />}
                                sx={{
                                    '.MuiChip-icon': {
                                        mx: 'auto',
                                        color: 'common.white',
                                        fontSize: 16,
                                        strokeWidth: 2,
                                        stroke: 'currentColor',
                                    },
                                    '.MuiChip-label': { display: 'none' },
                                }}
                            />
                        ) : (
                            <PortfolioItemProgressScoreChip
                                withIcon
                                isMandatory={!!child?.metadata?.isMandatory}
                                portfolioItem={child}
                                receivedCertificateValidationStatus={
                                    child?.metadata?.validationStatus ??
                                    undefined
                                }
                            />
                        )}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};
